import { useState, useCallback } from 'react';
import { pdfEngine } from '../../core/PDFEngine';
import { useDocumentStore } from '../../stores/documentStore';
import { useUIStore } from '../../stores/uiStore';

interface SearchResult {
  pageNumber: number;
  snippet: string;
}

export function SearchPanel() {
  const { totalPages, setCurrentPage } = useDocumentStore();
  const { toggleSearchPanel } = useUIStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim() || totalPages === 0) return;
    setSearching(true);
    setResults([]);
    setCurrentIndex(-1);

    try {
      const matches: SearchResult[] = [];
      const regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const textContent = await pdfEngine.getTextContent(pageNum);
        const pageText = textContent.items
          .filter((item: any) => 'str' in item)
          .map((item: any) => item.str)
          .join(' ');

        let match;
        while ((match = regex.exec(pageText)) !== null) {
          const start = Math.max(0, match.index - 25);
          const end = Math.min(pageText.length, match.index + query.length + 25);
          const snippet = `...${pageText.slice(start, end)}...`;
          matches.push({ pageNumber: pageNum, snippet });
          if (matches.length >= 100) break;
        }
        if (matches.length >= 100) break;
      }

      setResults(matches);
      if (matches.length > 0) {
        setCurrentIndex(0);
        setCurrentPage(matches[0].pageNumber);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }, [query, totalPages, setCurrentPage]);

  const handleNextMatch = () => {
    if (results.length === 0) return;
    const nextIdx = (currentIndex + 1) % results.length;
    setCurrentIndex(nextIdx);
    setCurrentPage(results[nextIdx].pageNumber);
  };

  const handlePrevMatch = () => {
    if (results.length === 0) return;
    const prevIdx = (currentIndex - 1 + results.length) % results.length;
    setCurrentIndex(prevIdx);
    setCurrentPage(results[prevIdx].pageNumber);
  };

  return (
    <div className="search-panel" style={{ flexWrap: 'wrap', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <span style={{ fontSize: '14px' }}>🔍</span>
        <input
          className="input"
          type="text"
          placeholder="Search in document..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          autoFocus
        />
        <button className="btn btn-primary btn-sm" onClick={handleSearch} disabled={searching}>
          {searching ? 'Searching...' : 'Find'}
        </button>
        {results.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button className="btn btn-ghost btn-sm" onClick={handlePrevMatch} title="Previous Match">
              ▲
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleNextMatch} title="Next Match">
              ▼
            </button>
            <span className="search-results-info">
              {currentIndex + 1} of {results.length}
            </span>
          </div>
        )}
      </div>

      <button
        className="btn btn-ghost btn-icon btn-sm"
        onClick={toggleSearchPanel}
        title="Close"
      >
        ×
      </button>

      {results.length > 0 && (
        <div
          style={{
            width: '100%',
            maxHeight: '120px',
            overflowY: 'auto',
            background: 'var(--color-bg-tertiary)',
            borderRadius: '6px',
            padding: '4px',
            fontSize: '12px',
          }}
        >
          {results.map((res, idx) => (
            <div
              key={idx}
              onClick={() => {
                setCurrentIndex(idx);
                setCurrentPage(res.pageNumber);
              }}
              style={{
                padding: '4px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: idx === currentIndex ? 'var(--color-accent-subtle)' : 'transparent',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                {res.snippet}
              </span>
              <span style={{ fontWeight: 'bold', marginLeft: '8px', color: 'var(--color-accent)' }}>
                Page {res.pageNumber}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
